import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { hiddenLanes, toggleLane } from '../engine/log/lanes';
import { isTerrainAnimation } from '../engine/battlefield/terrainAnimation';
import { enumValue, settingByKey, settingsInSection, viewerChoice } from '../engine/settings/registry';
import { isMarkerStatusLevel } from '../engine/battlefield/markerStatus';
import { getViewerPrefs, setViewerPref, subscribeViewerPrefs } from '../utils/viewerPrefs';
import useCurtain, { useCurtainBorrow } from './useCurtain';

/**
 * The stored preferences, live.
 *
 * ONE STORE, however many components ask. These are reachable from the
 * map's toolbar AND from Settings › Mine since v8.61.0, and two
 * `useState` copies of the same localStorage blob simply disagree - you
 * would tick a box in Settings and watch the map ignore it.
 */
function usePrefs() {
  return useSyncExternalStore(subscribeViewerPrefs, getViewerPrefs, getViewerPrefs);
}

/**
 * How each per-viewer map preference reads out of the stored blob - a
 * table, one reader per key, each with its default and the reason for it.
 */
const MAP_PREFS = {
  /**
   * How much a marker says about the person standing on it.
   *
   * MINIMAL by default: the wound ring and the condition marks are what
   * change how you play a turn, and they cost no text. The numbers and
   * the magazine are a step further in, because they only matter to
   * whoever is about to shoot.
   *
   * Validated rather than trusted - a later build may have written a
   * level this one has never heard of, and localStorage survives the
   * downgrade.
   */
  markerStatus: (p) => (isMarkerStatusLevel(p.markerStatus) ? p.markerStatus : 'minimal'),
  /** Names on every marker. OFF: a marker is a shape and a colour, and the panels say the rest. */
  showLabels: (p) => p.showLabels === true,
  /**
   * Measuring at somebody makes them your target.
   *
   * ON unless it has been turned off: measuring to a combatant is a
   * deliberate act and it is nearly always the person you are about to
   * shoot. Per viewer, like the labels, because it decides what YOUR
   * panels show - the GM and a player will want different answers.
   */
  autoTarget: (p) => p.autoTarget !== false,
  /**
   * Hovering says what you are looking at.
   *
   * ON unless turned off: a GM who set a material's hardness a fortnight
   * ago has no other way to ask what they chose.
   */
  hoverInfo: (p) => p.hoverInfo !== false,
  /**
   * And what it means for the shot you are about to take.
   *
   * OFF by default: a second layer of numbers, useful when you are
   * working out an angle and noise the rest of the time.
   */
  hoverDetail: (p) => p.hoverDetail === true,
  /**
   * Hatching on dangerous and impassable ground.
   *
   * OFF by default. The marks were added so a hazard could not be missed
   * (v7.71.0) and so blocked ground could not pass for open (v7.74.0) -
   * but a GM who has painted their own terrain has already said what it
   * looks like, and a cross-hatch over the top argues with them.
   */
  terrainMarks: (p) => p.terrainMarks === true,
  /**
   * Other people's pings.
   *
   * ON by default - the whole point is that they reach you. Per viewer,
   * so somebody who finds them noisy can mute them without taking the
   * gesture away from the rest of the table, which the GM's lock does.
   */
  showPings: (p) => p.showPings !== false,
  /** The take-cover cue (2026-10-25): ON by default - the whole point is to invite cover. */
  coverCue: (p) => p.coverCue !== false,
  /** How loud the movement bands are drawn: the registry's row decides the default (Quiet) and what is valid. */
  movementBands: (p) => enumValue(settingByKey('movementBands'), p.movementBands),
  /**
   * How terrain resolves as it appears - one of `TERRAIN_ANIMATIONS`.
   *
   * NONE by default. The map is a working surface before it is a
   * spectacle, and a GM loading terrain mid-fight should not have to
   * wait on an effect they did not ask for. That also settles the
   * `prefers-reduced-motion` question the old boolean needed a media
   * query for (v7.98.0): the default is already the least-motion answer
   * there is, so there is nothing left for the query to decide.
   *
   * MIGRATION. This was a boolean until v7.99.0, and somebody who had
   * switched it ON said what they wanted - they keep the sweep.
   */
  terrainAnimation: (p) => {
    if (isTerrainAnimation(p.terrainAnimation)) return p.terrainAnimation;
    return p.animateTerrain === true ? 'sweep' : 'none';
  },
};

const setPref = (key) => (v) => setViewerPref(key, v);
const togglePref = (key, current) => () => setViewerPref(key, !current);

/**
 * Per-viewer map preferences, in the same store as the log overlay.
 *
 * These are habits, not table rules: whether YOU want names on the map
 * is nobody else's business, so they never sync.
 */
export function useMapPrefs() {
  const stored = usePrefs();
  const p = Object.fromEntries(Object.entries(MAP_PREFS).map(([key, read]) => [key, read(stored)]));
  return {
    markerStatus: p.markerStatus,
    setMarkerStatus: setPref('markerStatus'),
    showLabels: p.showLabels,
    setShowLabels: setPref('showLabels'),
    toggleLabels: togglePref('showLabels', p.showLabels),
    autoTarget: p.autoTarget,
    toggleAutoTarget: togglePref('autoTarget', p.autoTarget),
    hoverInfo: p.hoverInfo,
    toggleHoverInfo: togglePref('hoverInfo', p.hoverInfo),
    hoverDetail: p.hoverDetail,
    toggleHoverDetail: togglePref('hoverDetail', p.hoverDetail),
    terrainMarks: p.terrainMarks,
    toggleTerrainMarks: togglePref('terrainMarks', p.terrainMarks),
    showPings: p.showPings,
    toggleShowPings: togglePref('showPings', p.showPings),
    terrainAnimation: p.terrainAnimation,
    setTerrainAnimation: setPref('terrainAnimation'),
    movementBands: p.movementBands,
    coverCue: p.coverCue,
  };
}

const NO_TABLE_DEFAULTS = {};

/**
 * The viewer's display options (plan map-display, C): their own choice in
 * My display; with none, the table's default (`tableDefaults`, off the house
 * rules); with neither, what shipped. Never synced: each screen its own.
 */
export function useDisplayPrefs(tableDefaults = NO_TABLE_DEFAULTS) {
  const stored = usePrefs();
  return Object.fromEntries(
    DISPLAY_ROWS.map(([name, def]) => [name, viewerChoice(def, stored[def.key], tableDefaults)]),
  );
}

/** My display's rows, each by its short name: `displayNumbersOff` reads as `numbersOff`. */
const DISPLAY_ROWS = settingsInSection('mine-display').map((def) => {
  const rest = def.key.replace(/^display/, '');
  return [rest[0].toLowerCase() + rest.slice(1), def];
});

/**
 * useLogOverlay - state for the combat log that hangs over the tactical map.
 *
 * The two logs used to be stacked with a draggable divider between them,
 * each getting half a panel it rarely needed. They are now one scroller
 * with a view switch, hanging over the map from its foot and pulled up or
 * rolled down by its edge (plan design-gaps L3).
 *
 * This is the app's first persisted layout state: how much map you want to
 * see while you read is a habit, not a per-session decision.
 *
 * `view` is the RAW preference. The panel shows the shared log only when
 * there is a session to share with, so callers resolve the effective view
 * with `resolveLogView` rather than reading `view` directly.
 */
export default function useLogOverlay() {
  // Through the same store as the map's preferences, so the log's view
  // can also be chosen from Settings › My log without the two copies
  // drifting apart.
  const prefs = usePrefs();
  // THE BORROW IS MADE HERE, the curtain later (useLogCurtain): the shell's
  // sync yields the log before the shell lays it out (plan design-gaps, L3).
  const borrow = useCurtainBorrow();
  const view = prefs.view === 'local' ? 'local' : 'shared';
  const setView = useCallback((v) => setViewerPref('view', v), []);
  // The lanes this viewer switched off (v9.7.0, engine/log/lanes; the Talk filter of v8.363.4 before).
  const lanesOff = useMemo(() => hiddenLanes(prefs), [prefs]);
  const toggleLogLane = useCallback(
    (lane) => setViewerPref('logLanesOff', toggleLane(hiddenLanes(getViewerPrefs()), lane)),
    [],
  );
  // A toast for a private vox or a mention (v8.363.5) - on unless turned off.
  const chatToasts = prefs.chatToasts !== false;
  // Volleys open, every one (v9.7.0): they are drawn as one line each unless the viewer asks.
  const logExpandAll = prefs.logExpandAll === true;
  const toggleLogExpandAll = useCallback(() => setViewerPref('logExpandAll', !getViewerPrefs().logExpandAll), []);

  return { view, setView, lanesOff, toggleLogLane, logExpandAll, toggleLogExpandAll, chatToasts, borrow };
}

/**
 * The log's curtain hangs from the FOOT of the map, rolled up to its ticker
 * by default and opening upward (the session page's "the log as a ticker";
 * plan design-gaps, L3). Its keys are the rails' own since the classic
 * columns hung theirs from the top under `height` and `collapsed`; those
 * stored values are simply never read (plan retire-classic, v12.0.0).
 */
const LOG_CURTAIN = {
  heightKey: 'railsLogHeight',
  collapsedKey: 'railsLogCollapsed',
  containerTestId: 'log-overlay',
  defaultHeight: 46,
  defaultCollapsed: true,
  dock: 'bottom',
};

/**
 * The log's curtain: the shell's chrome made the borrow (useLogOverlay).
 *
 * @param {object} opts
 * @param {object} opts.borrow - useLogOverlay's `borrow`
 */
export function useLogCurtain({ borrow }) {
  return useCurtain({ ...LOG_CURTAIN, borrow });
}

/**
 * The log actually on screen. Solo play has no shared log at all, so the
 * preference is ignored until a session exists - otherwise a lone player
 * would watch their attacks land in a tab they cannot see.
 *
 * @param {string} view      raw preference ('shared' | 'local')
 * @param {string|null} sessionId
 * @returns {'shared'|'local'}
 */
export function resolveLogView(view, sessionId) {
  return sessionId && view === 'shared' ? 'shared' : 'local';
}
